<script lang="ts">
  import type { CompareField, CompareSeries } from '../api/compare'
  import { frame } from '../chart/frame'
  import { colorFor, widthFor } from '../chart/palette'
  import { line, stepLine, type Point } from '../chart/path'
  import { linear, niceDomain, niceTicks, time } from '../chart/scale'
  import { timeTicks } from '../chart/ticks'
  import { differsFromViewer, formatInZone, MINUTE, zoneLabel } from '../time'

  /**
   * The meteogram. The only importer of `lib/chart`, the way `MapView` is the
   * only importer of `lib/map` — the geometry is pure functions a test calls
   * directly, and this is the one thing that knows about the DOM.
   *
   * **SVG rather than canvas, deliberately.** `web/tests/` mounts the whole
   * tree in jsdom and asserts on roles and text, and `make e2e` reads the DOM
   * and the screenshots. A canvas is one opaque element to both, and this repo
   * has already shipped a blank map with a green suite once. An SVG chart's
   * lines, marks and labels are all assertable nodes — and it inherits the
   * app's CSS custom properties instead of needing them read back out.
   *
   * The x axis is in the **point's** zone, not the viewer's, and says so when
   * they differ: a diurnal curve marked against the wrong midnight puts night
   * in the wrong place, and the day boundary is the axis's most useful cue.
   */

  interface Props {
    series: CompareSeries[]
    field: CompareField
    from: Date
    to: Date
    timezone?: string | null
    /** Series the reader has switched off in the legend. */
    hidden?: ReadonlySet<string>
    /** The instant the map is drawing, if a model layer is active. Read-only:
     *  the chart never moves the slider. See plan 7.5. */
    cursor?: Date | null
    /** The answer's own clock: the past is shaded up to here, and marked. */
    now?: Date | null
    height?: number
  }

  let {
    series,
    field,
    from,
    to,
    timezone = null,
    hidden = new Set<string>(),
    cursor = null,
    now = null,
    height = 190,
  }: Props = $props()

  /**
   * The narrowest this panel ever is, and the width to draw at before the
   * container has been measured.
   *
   * jsdom reports 0 for `clientWidth`, so without a floor every test would
   * assert against a chart one pixel wide — which is a green suite and no
   * chart, the failure mode this component is SVG to avoid.
   */
  const MIN_WIDTH = 276

  let box = $state(0)
  let width = $derived(Math.max(MIN_WIDTH, box))
  // Room above the plot for the "Now" label, and a left margin that fits a
  // flush-left "105°" (round 2 draws the y labels from the chart's edge).
  let f = $derived(frame(width, height, { top: 18, left: 32, right: 6 }))

  let visible = $derived(series.filter((s) => !hidden.has(s.id)))

  /** Only the points inside the window, and only ones with a real instant. */
  function inWindow(s: CompareSeries): Point[] {
    const lo = from.getTime()
    const hi = to.getTime()
    const out: Point[] = []
    for (const p of s.points) {
      const t = Date.parse(p.t)
      if (Number.isNaN(t) || t < lo || t > hi) continue
      out.push({ t, v: typeof p.v === 'number' ? p.v : null })
    }
    return out
  }

  /** One series, ready to draw: its points, its slot colour and its span. */
  let drawn = $derived(
    visible.map((s) => ({
      series: s,
      points: inWindow(s),
      color: colorFor(s.kind, s.id),
      stroke: widthFor(s.kind),
      windowMs: (s.window_minutes || field.window_minutes || 0) * MINUTE,
    })),
  )

  let forecasts = $derived(drawn.filter((d) => d.series.kind !== 'observed'))
  let observed = $derived(drawn.find((d) => d.series.kind === 'observed') ?? null)

  /**
   * The y domain is the union of what is *visible*, padded — never the field's
   * `plotted` range. A temperature colormap plots -40 to 110 °F and one
   * September afternoon drawn over that is a flat line across the middle.
   *
   * An accumulation includes zero, because a rainfall step that does not start
   * from the floor reads as a much bigger difference than it is.
   */
  let yDomain = $derived.by(() => {
    const values = drawn.flatMap((d) => d.points.map((p) => p.v))
    // An accumulation is drawn from zero: a rainfall step that does not start
    // at the floor reads as a much bigger difference than it is. It is also
    // the one case with a *hard* floor — an hour of rain cannot be negative,
    // and padding the axis below zero is the chart making a claim.
    const accumulation = field.window_minutes > 0
    if (accumulation) values.push(0)
    return niceDomain(values, {
      minSpan: field.decimals > 0 ? 0.1 : 2,
      floor: accumulation ? 0 : undefined,
    })
  })

  let sx = $derived(time([from, to], f.xRange))
  let sy = $derived(linear(yDomain, f.yRange))

  let yMarks = $derived(niceTicks(yDomain[0], yDomain[1], f.yTicks))
  let xMarks = $derived(timeTicks(from, to, f.xTicks, timezone))

  /**
   * The observed trace, thinned to dots a reader can tell apart.
   *
   * A major airport reports every five minutes — 145 observations in twelve
   * hours — and 145 eight-pixel dots across 130 px is a bar, not a series.
   * Every dot drawn is a *real observation*: the thinning keeps points and
   * never averages them, because averaging two measurements produces a number
   * nobody measured. The count is stated in the legend.
   */
  const MAX_DOTS = 24

  /** The observed mark's side: a square, as round 2 draws it. */
  const DOT = 5

  let dots = $derived.by(() => {
    if (!observed) return []
    const usable = observed.points.filter((p) => p.v !== null)
    if (usable.length <= MAX_DOTS) return usable
    const stride = (usable.length - 1) / (MAX_DOTS - 1)
    return Array.from({ length: MAX_DOTS }, (_, i) => usable[Math.round(i * stride)])
  })

  // ---------- the hover layer ----------

  let hoverX = $state<number | null>(null)

  let hovered = $derived.by(() => {
    if (hoverX === null) return null
    const at = sx.invertTime(hoverX).getTime()
    const rows = drawn
      .map((d) => {
        const near = nearest(d.points, at, d.windowMs || 90 * MINUTE)
        return near && near.v !== null
          ? { id: d.series.id, label: d.series.label, color: d.color, value: near.v, at: near.t }
          : null
      })
      .filter((row): row is NonNullable<typeof row> => row !== null)
    if (rows.length === 0) return null
    return { at, rows, x: sx(at) }
  })

  function nearest(points: readonly Point[], at: number, tolerance: number): Point | null {
    let best: Point | null = null
    let bestGap = Infinity
    for (const p of points) {
      const gap = Math.abs(p.t - at)
      if (gap < bestGap) {
        best = p
        bestGap = gap
      }
    }
    return best && bestGap <= tolerance ? best : null
  }

  function onMove(event: PointerEvent & { currentTarget: SVGSVGElement }) {
    const rect = event.currentTarget.getBoundingClientRect()
    if (rect.width === 0) return
    // The SVG scales to its box, so a client pixel is not a user unit.
    const x = ((event.clientX - rect.left) / rect.width) * width
    hoverX = x >= f.plot.x && x <= f.plot.x + f.plot.width ? x : null
  }

  function fmt(value: number): string {
    return value.toFixed(field.decimals)
  }

  /** An axis label: a temperature carries its degree sign, as drawn. */
  function axisLabel(value: number): string {
    return field.units.startsWith('°') ? `${fmt(value)}°` : fmt(value)
  }

  function clockAt(at: number): string {
    return formatInZone(new Date(at), timezone, { hour: 'numeric', minute: '2-digit' })
  }

  // ---------- the accessible summary ----------

  let zone = $derived(zoneLabel(from, timezone))
  let otherZone = $derived(differsFromViewer(from, timezone))

  /**
   * What a screen reader hears instead of the picture. The table carries the
   * numbers (`aria-describedby`), so this is the shape: how many sources, over
   * what window, and how far apart they are — which is the question the chart
   * exists to answer.
   */
  let summary = $derived.by(() => {
    const names = forecasts.map((d) => d.series.label).join(', ')
    const values = forecasts.flatMap((d) => d.points.map((p) => p.v)).filter((v) => v !== null)
    if (values.length === 0) return `${field.label}: no forecast to draw here.`
    const low = Math.min(...(values as number[]))
    const high = Math.max(...(values as number[]))
    return (
      `${field.label} in ${field.units} from ${names}. ` +
      `Between ${fmt(low)} and ${fmt(high)} over the window shown.`
    )
  })

  let cursorX = $derived(cursor && cursor >= from && cursor <= to ? sx(cursor.getTime()) : null)
  let nowX = $derived(now && now >= from && now <= to ? sx(now.getTime()) : null)
</script>

<div class="chart" bind:clientWidth={box}>
  <svg
    viewBox="0 0 {width} {height}"
    width="100%"
    {height}
    role="img"
    aria-label={summary}
    onpointermove={onMove}
    onpointerleave={() => (hoverX = null)}
  >
    {#if nowX !== null}
      <!-- What already happened, in a faint wash: the observed squares live
           here, and nothing to the left of the line is a forecast any more. -->
      <rect
        class="past"
        x={f.plot.x}
        y={f.plot.y}
        width={Math.max(0, nowX - f.plot.x)}
        height={f.plot.height}
      />
    {/if}

    <!-- Gridlines: hairline, solid, the divider. Recessive by construction;
         they are scaffolding, not data. -->
    <g class="grid" aria-hidden="true">
      {#each yMarks as mark (mark)}
        <line x1={f.plot.x} x2={f.plot.x + f.plot.width} y1={sy(mark)} y2={sy(mark)} />
      {/each}
      <line
        x1={f.plot.x}
        x2={f.plot.x + f.plot.width}
        y1={f.plot.y + f.plot.height}
        y2={f.plot.y + f.plot.height}
      />
    </g>

    <g class="axis y" aria-hidden="true">
      {#each yMarks as mark (mark)}
        <text x={0} y={sy(mark) + 3.5}>{axisLabel(mark)}</text>
      {/each}
    </g>

    <g class="axis x" aria-hidden="true">
      {#each xMarks as mark (mark.at.getTime())}
        {#if mark.day}
          <!-- Local midnight: the axis's most useful cue, dotted in ink. -->
          <line
            class="day"
            x1={sx(mark.at.getTime())}
            x2={sx(mark.at.getTime())}
            y1={f.plot.y}
            y2={f.plot.y + f.plot.height}
          />
        {/if}
        <text
          x={sx(mark.at.getTime())}
          y={height - 4}
          text-anchor={sx(mark.at.getTime()) - f.plot.x < 12
            ? 'start'
            : f.plot.x + f.plot.width - sx(mark.at.getTime()) < 12
              ? 'end'
              : 'middle'}
          class:day={mark.day}>{mark.label}</text
        >
      {/each}
    </g>

    {#each forecasts as d (d.series.id)}
      <path
        class="series"
        class:official={d.series.kind === 'official'}
        data-series={d.series.id}
        d={d.windowMs > 0
          ? stepLine(d.points, (t) => sx(t), (v) => sy(v), d.windowMs)
          : line(d.points, (t) => sx(t), (v) => sy(v))}
        stroke={d.color}
        stroke-width={d.stroke}
        fill="none"
      />
    {/each}

    <!-- A forecast and a measurement are different kinds of thing, so they are
         drawn differently rather than in two colours: squares, and only ever
         in the past. -->
    <g class="observed" data-series={observed?.series.id ?? ''}>
      {#each dots as dot (dot.t)}
        <rect x={sx(dot.t) - DOT / 2} y={sy(dot.v as number) - DOT / 2} width={DOT} height={DOT} />
      {/each}
    </g>

    {#if nowX !== null}
      <line class="now" x1={nowX} x2={nowX} y1={f.plot.y - 6} y2={f.plot.y + f.plot.height} />
      <text class="now-label" x={nowX + 4} y={f.plot.y - 8} aria-hidden="true">Now</text>
    {/if}

    {#if cursorX !== null}
      <!-- The instant the map is drawing. Read-only: a second writer on the
           frame axis is what the one-clock rule exists to prevent. -->
      <line
        class="cursor"
        x1={cursorX}
        x2={cursorX}
        y1={f.plot.y}
        y2={f.plot.y + f.plot.height}
      />
    {/if}

    {#if hovered}
      <g class="hover" aria-hidden="true">
        <line x1={hovered.x} x2={hovered.x} y1={f.plot.y} y2={f.plot.y + f.plot.height} />
        {#each hovered.rows as row (row.id)}
          <rect
            x={sx(row.at) - 3.5}
            y={sy(row.value) - 3.5}
            width="7"
            height="7"
            fill={row.color}
          />
        {/each}
      </g>
    {/if}
  </svg>

  {#if hovered}
    <div
      class="tooltip"
      style:left="{(hovered.x / width) * 100}%"
      style:--side={hovered.x > width / 2 ? '-100%' : '0%'}
      aria-hidden="true"
    >
      <div class="when">{clockAt(hovered.at)}</div>
      {#each hovered.rows as row (row.id)}
        <div class="row">
          <span class="key" style:background={row.color}></span>
          <span class="name">{row.label}</span>
          <span class="value">{fmt(row.value)}</span>
        </div>
      {/each}
    </div>
  {/if}

  {#if zone}
    <!-- The chart is in the *point's* zone and every other panel is in the
         viewer's, so it always says which; the reason is printed only when
         the two differ. -->
    <p class="zone">Axis in {zone}{otherZone ? " (point's time)" : ''}</p>
  {/if}
</div>

<style>
  .chart {
    position: relative;
    width: 100%;
  }

  .zone {
    margin: 6px 0 0;
    font-size: var(--hint-fs, 11px);
    color: var(--color-muted);
  }

  svg {
    display: block;
    overflow: visible;
    /* The hover layer wants every pointer event; the SVG has no other job. */
    touch-action: pan-y;
  }

  .past {
    fill: color-mix(in srgb, var(--color-text) 6%, transparent);
  }

  .grid line {
    stroke: var(--color-divider);
    stroke-width: 1;
  }

  .axis text {
    fill: var(--color-muted);
    font-size: 10px;
    /* Text never wears the data colour: identity comes from the legend key. */
  }

  .axis text.day {
    fill: var(--color-text);
    font-weight: 800;
  }

  .axis.x line.day {
    stroke: var(--color-text);
    stroke-width: 1;
    stroke-dasharray: 2 3;
  }

  .series {
    stroke-linejoin: round;
    stroke-linecap: square;
  }

  .observed rect {
    fill: var(--color-success);
  }

  .now {
    stroke: var(--color-text);
    stroke-width: 2;
  }

  .now-label {
    fill: var(--color-text);
    font-size: 10px;
    font-weight: 800;
  }

  .cursor {
    stroke: var(--color-accent);
    stroke-width: 1.5;
  }

  .hover line {
    stroke: var(--color-text);
    stroke-width: 1;
    opacity: 0.35;
  }

  .hover rect {
    stroke: var(--color-bg);
    stroke-width: 2;
  }

  .tooltip {
    position: absolute;
    top: 0;
    transform: translateX(var(--side));
    pointer-events: none;
    background: var(--color-bg);
    border: 2px solid var(--color-text);
    box-shadow: var(--shadow-md);
    padding: 5px 8px;
    font-size: 11px;
    min-width: 104px;
    z-index: 2;
  }

  .when {
    color: var(--color-muted);
    margin-bottom: 2px;
  }

  .tooltip .row {
    display: flex;
    align-items: center;
    gap: 6px;
    white-space: nowrap;
  }

  .key {
    width: 8px;
    height: 8px;
    flex: 0 0 8px;
  }

  .name {
    flex: 1 1 auto;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .value {
    font-variant-numeric: tabular-nums;
    font-weight: 800;
  }
</style>
